/**
 * Barkod okutma eşleştirme yardımcıları.
 *
 * Daha önce her ekranda (CountingFlow, PickingActiveFlow, PickingConsolidatedFlow,
 * PickingSortingFlow, PackingProcess, ReceivingPutaway) kopya olarak duran
 * normalize/eşleştirme fonksiyonlarının tek kaynağı.
 */

export const normalizeScanValue = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');

export const buildBarcodeVariants = (value: unknown): Set<string> => {
  const normalized = normalizeScanValue(value);
  const variants = new Set<string>();
  if (!normalized) return variants;

  variants.add(normalized);
  const digitsOnly = normalized.replace(/\D/g, '');
  if (digitsOnly) {
    variants.add(digitsOnly);
    // UPC-A (12) <-> EAN-13 (0 + 12) farki icin tolerans.
    if (digitsOnly.length === 12) variants.add(`0${digitsOnly}`);
    if (digitsOnly.length === 13 && digitsOnly.startsWith('0')) variants.add(digitsOnly.slice(1));
  }
  return variants;
};

export const matchesScannedValue = (scanned: unknown, candidate: unknown): boolean => {
  const scannedVariants = buildBarcodeVariants(scanned);
  const candidateVariants = buildBarcodeVariants(candidate);
  if (scannedVariants.size === 0 || candidateVariants.size === 0) return false;
  return Array.from(scannedVariants).some((variant) => candidateVariants.has(variant));
};

/**
 * Depo konumu (peron/raf) eşleştirmesi: ürün barkodlarından farklı olarak
 * ayraç karakterlerine (-, /, _, .) tolerans gösterir. Böylece etiketteki
 * "PERON021-RAF004", okutucudan gelen "Peron021Raf004" veya elle yazılan
 * "peron 021 / raf 004" aynı konuma çözümlenir. Eski "{Depo}-{Kod}" formatı
 * etiketleri de bu yolla çalışmaya devam eder.
 */
export const buildLocationScanVariants = (value: unknown): Set<string> => {
  const variants = buildBarcodeVariants(value);
  const normalized = normalizeScanValue(value);
  if (normalized) {
    const alphanumeric = normalized.replace(/[^A-Z0-9ÇĞİÖŞÜ]/g, '');
    if (alphanumeric) variants.add(alphanumeric);
  }
  return variants;
};

export const matchesScannedLocation = (scanned: unknown, candidate: unknown): boolean => {
  const scannedVariants = buildLocationScanVariants(scanned);
  const candidateVariants = buildLocationScanVariants(candidate);
  if (scannedVariants.size === 0 || candidateVariants.size === 0) return false;
  return Array.from(scannedVariants).some((variant) => candidateVariants.has(variant));
};
